import { useState } from 'react';
import { Link } from 'react-router-dom';
import Exports from './Exports';
import { api, can, getUser } from '../lib/api';
import { ACTION_TONE, dateTime, dateTimeUtc, localZoneLabel, number, relative } from '../lib/format';
import {
  Card, Empty, ErrorNote, Hash, Loading, Modal, PageHead, Pill, useResource, useToast,
} from '../components/ui';

/**
 * CM-4 Consent ledger (wireframe W6).
 *
 * The evidence heart of the module: an append-only, hash-chained event stream.
 * This is the screen an advocate looks at before exporting for the Board, so it
 * shows the chain links, not just the outcome.
 */

interface LedgerEvent {
  event_id: string;
  seq: string;
  principal_hash: string;
  purpose_id: string | null;
  purpose_version: number | null;
  purpose_name: string | null;
  notice_id: string | null;
  notice_version: number | null;
  lang: string | null;
  content_hash: string | null;
  action: string;
  channel: string;
  collection_point_id: string | null;
  occurred_at: string;
  evidence: Record<string, unknown>;
  prev_event_hash: string;
  event_hash: string;
  signed?: boolean;
  batch_no?: number | null;
  batched_at?: string | null;
  timestamped_at?: string | null;
}

interface SigningStatus {
  issuer: { id: string; name: string };
  keys: Array<{ kid: string; alg: string; status: string; activatedAt: string; retiredAt: string | null }>;
  events: number;
  signed: number;
  unsigned: number;
  batches: number;
  batchedThrough: number;
  latestBatch: { number: number; lastSeq: number; root: string; createdAt: string; timestampedAt: string | null } | null;
  timestampAuthority: 'configured' | 'not_set';
}

interface VerifyRun {
  id: string;
  finished_at: string;
  events_checked: string;
  status: string;
  break_seq: string | null;
  break_reason: string | null;
}

const ACTIONS = [
  'granted', 'declined', 'withdrawn', 'expired', 'refreshed',
  'informed', 'requested', 're_requested', 'modified',
  'notice_viewed', 'preference_centre_viewed', 'identity_verified', 'browser_linked',
  'erasure_requested', 'erasure_completed', 'erasure_notice_sent',
];

export default function Ledger() {
  const toast = useToast();
  const canExport = can(getUser(), 'ledger:export');
  const [filters, setFilters] = useState({ identifier: '', purposeId: '', action: '' });
  const [applied, setApplied] = useState({ principalHash: '', purposeId: '', action: '' });
  const [looking, setLooking] = useState(false);

  // The person is looked up by email or phone; only the hash reaches the query.
  async function search() {
    let principalHash = '';
    if (filters.identifier.trim()) {
      setLooking(true);
      try {
        const res = await api.get<{ principalHash: string; found: boolean }>(`/v1/principals/lookup?identifier=${encodeURIComponent(filters.identifier.trim())}`);
        principalHash = res.principalHash;
      } catch (err) {
        toast((err as Error).message, 'bad');
        setLooking(false);
        return;
      }
      setLooking(false);
    }
    setApplied({ principalHash, purposeId: filters.purposeId, action: filters.action });
  }
  const [detail, setDetail] = useState<LedgerEvent | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [nonce, setNonce] = useState(0);

  const query = new URLSearchParams({ limit: '100' });
  if (applied.principalHash) query.set('principalHash', applied.principalHash);
  if (applied.purposeId) query.set('purposeId', applied.purposeId);
  if (applied.action) query.set('action', applied.action);

  const events = useResource<{ events: LedgerEvent[]; nextBefore: number | null }>(
    () => api.get(`/v1/ledger?${query.toString()}`),
    [applied.principalHash, applied.purposeId, applied.action, nonce],
  );
  const purposes = useResource<{ purposes: Array<{ id: string; current: { name: string } }> }>(
    () => api.get('/v1/purposes'),
  );
  const runs = useResource<{ runs: VerifyRun[] }>(() => api.get('/v1/ledger/verify'), [nonce]);

  async function verify() {
    setVerifying(true);
    try {
      const res = await api.post<{ status: string; eventsChecked: number; break?: { reason: string } }>(
        '/v1/ledger/verify',
      );
      toast(
        res.status === 'intact'
          ? `Chain intact across ${number(res.eventsChecked)} events.`
          : `CHAIN BROKEN: ${res.break?.reason}`,
        res.status === 'intact' ? 'ok' : 'bad',
      );
      setNonce((n) => n + 1);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setVerifying(false);
    }
  }

  const last = runs.data?.runs[0];

  return (
    <>
      <PageHead
        title="Consent ledger"
      >
        {/* Consent arrives while this screen is open, so it needs a way to catch up
            without the operator having to fiddle with a filter to force a refetch. */}
        <button onClick={() => setNonce((n) => n + 1)}>Refresh</button>
        <button onClick={verify} disabled={verifying}>{verifying ? 'Verifying…' : 'Verify records'}</button>

      </PageHead>

      <ErrorNote error={events.error} />

      {last && (
        <div className={`note ${last.status === 'intact' ? 'ok' : 'bad'}`} style={{ marginBottom: 14 }}>
          <strong>{last.status === 'intact' ? 'Records verified intact' : 'Records altered'}</strong>
          {' · '}{number(Number(last.events_checked))} events, {relative(last.finished_at)}
          {(() => {
            const latest = Number(events.data?.events[0]?.seq ?? 0);
            const unchecked = latest - Number(last.events_checked);
            return latest && unchecked > 0 ? <> · {number(unchecked)} newer events not yet verified</> : null;
          })()}
          {last.break_reason && <div style={{ marginTop: 4 }}>Altered at entry {last.break_seq}: {last.break_reason}</div>}
        </div>
      )}

      <Card>
        <div className="row" style={{ marginBottom: 14 }}>
          <div>
            <label className="small muted">Person</label>
            <input
              type="text" placeholder="Email address or mobile number" value={filters.identifier} autoComplete="off"
              onChange={(e) => setFilters({ ...filters, identifier: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
            />
          </div>
          <div>
            <label className="small muted">Purpose</label>
            <select value={filters.purposeId} onChange={(e) => setFilters({ ...filters, purposeId: e.target.value })}>
              <option value="">All purposes</option>
              {purposes.data?.purposes.map((p) => (
                <option key={p.id} value={p.id}>{p.current.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="small muted">Action</label>
            <select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })}>
              <option value="">All actions</option>
              {ACTIONS.map((a) => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <div className="tight">
            <button className="primary" onClick={search} disabled={looking}>{looking ? 'Searching…' : 'Search'}</button>{' '}
            <button onClick={() => { setFilters({ identifier: '', purposeId: '', action: '' }); setApplied({ principalHash: '', purposeId: '', action: '' }); }}>
              Clear
            </button>
          </div>
        </div>

        {events.loading && !events.data && <Loading what="Loading events" />}

        {events.data && (
          <div className="small muted" style={{ marginBottom: 8 }}>
            {events.data.events.length} events, newest first.
          </div>
        )}

        {events.data && (events.data.events.length === 0 ? (
          <Empty>No events match the filters.</Empty>
        ) : (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Time ({localZoneLabel()})</th>
                  <th>Person</th>
                  <th>Purpose</th>
                  <th>Action</th>
                  <th>Channel</th>
                  <th>Notice</th>
                  <th>Signed</th>
                  <th>Batch</th>
                </tr>
              </thead>
              <tbody>
                {events.data.events.map((e) => (
                  <tr key={e.event_id} onClick={() => setDetail(e)} style={{ cursor: 'pointer' }}>
                    <td className="small muted">{e.seq}</td>
                    <td className="small" title={`Stored value: ${dateTimeUtc(e.occurred_at)}`}>{dateTime(e.occurred_at)}</td>
                    <td><Link to={`/principals/${e.principal_hash}`} onClick={(ev) => ev.stopPropagation()}>Record {e.principal_hash.slice(0, 8)}…</Link></td>
                    <td>
                      {e.purpose_name ?? <span className="muted">—</span>}
                      {e.purpose_version && <span className="small muted"> v{e.purpose_version}</span>}
                    </td>
                    <td><Pill tone={ACTION_TONE[e.action] || undefined}>{e.action.replace(/_/g, ' ')}</Pill></td>
                    <td className="small">{e.channel}</td>
                    <td className="small">{e.notice_version ? `v${e.notice_version} · ${e.lang}` : ''}</td>
                    <td>{e.signed ? <Pill tone="ok">Signed</Pill> : <Pill tone="warn">Pending</Pill>}</td>
                    <td className="small">{e.batch_no ? e.batch_no : <span className="muted">Pending</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </Card>

      <SigningKeys nonce={nonce} onRotated={() => setNonce((n) => n + 1)} />

      <Exports embedded />

      {detail && <EventDetail event={detail} onClose={() => setDetail(null)} />}
    </>
  );
}

const VERIFICATION_LABEL: Record<string, string> = {
  self_asserted: 'Self-asserted', fiduciary_attested: 'Attested by the organisation\'s system', otp: 'One-time code',
  campaign_link: 'Campaign link', operator: 'Recorded by operator', otp_voice: 'Code read back on the call',
  none: 'Operator attested, no code', system: 'System', guardian: 'Parent or guardian',
};

function EventDetail({ event, onClose }: { event: LedgerEvent; onClose: () => void }) {
  const toast = useToast();
  const ev = event.evidence ?? {};
  const operator = ev.surface === 'operator';
  const attachmentId = typeof ev.attachmentId === 'string' ? ev.attachmentId : null;
  return (
    <Modal
      wide
      title={`Event ${event.event_id}`}
      description="All fields are included in the content hash."
      onClose={onClose}
      footer={(
        <>
          {event.signed && (
            <button
              onClick={() => api.download(`/v1/ledger/events/${event.event_id}/receipt`, `receipt-${event.event_hash.slice(0, 12)}.json`)
                .catch((e) => toast((e as Error).message, 'bad'))}
            >
              Download receipt
            </button>
          )}
          <button onClick={onClose}>Close</button>
        </>
      )}
    >
      <table className="data">
        <tbody>
          <Row label="Sequence" value={<span className="mono">{event.seq}</span>} />
          <Row
            label={`Occurred at (${localZoneLabel()})`}
            value={<span className="mono">{dateTime(event.occurred_at)}</span>}
          />
          {/* The stored, hashed value. This is what an evidence export contains. */}
          <Row label="Occurred at (stored)" value={<span className="mono">{dateTimeUtc(event.occurred_at)}</span>} />
          <Row label="Action" value={<Pill tone={ACTION_TONE[event.action] || undefined}>{event.action.replace(/_/g, ' ')}</Pill>} />
          <Row label="Principal" value={<Hash value={event.principal_hash} head={24} />} />
          <Row
            label="Purpose"
            value={event.purpose_id ? `${event.purpose_name ?? event.purpose_id} (v${event.purpose_version})` : '—'}
          />
          <Row
            label="Notice shown"
            value={event.notice_id ? `${event.notice_id} v${event.notice_version} · ${event.lang}` : '—'}
          />
          <Row label="Notice content hash" value={<Hash value={event.content_hash} head={24} />} />
          <Row label="Channel" value={`${event.channel}${event.collection_point_id ? ` · ${event.collection_point_id}` : ''}`} />
          {typeof ev.bannerVersion === 'number' && (
            <Row
              label="Banner shown"
              value={<span>Version {ev.bannerVersion} · <Hash value={typeof ev.bannerHash === 'string' ? ev.bannerHash : null} head={16} /></span>}
            />
          )}
          {operator && (
            <>
              <Row label="Recorded by" value={String(ev.operator ?? '—')} />
              <Row label="Consent given at" value={typeof ev.capturedAt === 'string' ? <span className="mono">{dateTime(ev.capturedAt)}</span> : '—'} />
              <Row label="Verification" value={VERIFICATION_LABEL[String(ev.verification)] ?? String(ev.verification ?? '—')} />
              {typeof ev.formRef === 'string' && <Row label="Form or call reference" value={<span className="mono">{ev.formRef}</span>} />}
              {typeof ev.register === 'string' && <Row label="Register" value={`${ev.register}${ev.register_row ? ` · row ${ev.register_row}` : ''}`} />}
              {typeof ev.note === 'string' && <Row label="Operator note" value={ev.note} />}
              {attachmentId && (
                <Row
                  label="Scanned form"
                  value={(
                    <span>
                      <Hash value={typeof ev.attachment_sha256 === 'string' ? ev.attachment_sha256 : null} head={16} />{' '}
                      <button className="small" onClick={() => api.download(`/v1/consents/operator/attachments/${attachmentId}/download`, `${attachmentId}.pdf`).catch((e) => toast((e as Error).message, 'bad'))}>Download</button>
                    </span>
                  )}
                />
              )}
            </>
          )}
          <Row
            label="Evidence"
            value={<pre className="mono" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(event.evidence, null, 2)}</pre>}
          />
          <Row label="Previous hash" value={<Hash value={event.prev_event_hash} head={24} />} />
          <Row label="This event hash" value={<Hash value={event.event_hash} head={24} />} />
          <Row label="Signatures" value={event.signed ? 'ES256 and ML-DSA-65, over the event hash' : 'Not signed yet. The worker signs it within a minute.'} />
          <Row
            label="Batch"
            value={event.batch_no
              ? `Batch ${event.batch_no}, built ${dateTime(event.batched_at ?? '')}${event.timestamped_at ? `, timestamped ${dateTime(event.timestamped_at)}` : ''}`
              : 'Not in a batch yet. Batches are built every few minutes.'}
          />
        </tbody>
      </table>
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <tr>
      <td style={{ width: 190, color: 'var(--text-subtle)', fontSize: 12.5 }}>{label}</td>
      <td>{value}</td>
    </tr>
  );
}

/**
 * Ledger signing keys. Every event is signed with ES256 and ML-DSA-65; the
 * public halves are published so anyone can check a receipt. Rotation retires
 * the current pair (still published) and starts a new one.
 */
function SigningKeys({ nonce, onRotated }: { nonce: number; onRotated: () => void }) {
  const toast = useToast();
  const canRotate = can(getUser(), 'ledger:keys');
  const [confirming, setConfirming] = useState(false);
  const [rotating, setRotating] = useState(false);
  const status = useResource<SigningStatus>(() => api.get('/v1/ledger/signing'), [nonce]);

  async function rotate() {
    setRotating(true);
    try {
      await api.post('/v1/ledger/signing/rotate');
      toast('Signing keys rotated.', 'ok');
      setConfirming(false);
      onRotated();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setRotating(false);
    }
  }

  const s = status.data;
  return (
    <Card title="Signing keys">
      <ErrorNote error={status.error} />
      {status.loading && !s && <Loading what="Loading signing keys" />}
      {s && (
        <>
          <div className="small muted" style={{ marginBottom: 10 }}>
            Receipts name the issuer as {s.issuer.name} ({s.issuer.id}).
            {' '}{number(s.signed)} of {number(s.events)} events signed
            {s.unsigned > 0 ? ` (${number(s.unsigned)} waiting)` : ''}.
            {' '}{s.batches ? `Batched through entry ${number(s.batchedThrough)} in ${number(s.batches)} batches.` : 'No batches yet.'}
            {' '}Timestamp authority: {s.timestampAuthority === 'configured' ? 'configured' : 'not set'}.
          </div>
          {s.keys.length === 0 ? (
            <Empty>No keys yet. The first event signed creates them.</Empty>
          ) : (
            <div className="table-scroll">
              <table className="data">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Algorithm</th>
                    <th>Activated ({localZoneLabel()})</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {s.keys.map((k) => (
                    <tr key={k.kid}>
                      <td><Hash value={k.kid} head={16} /></td>
                      <td className="small">{k.alg}</td>
                      <td className="small">{dateTime(k.activatedAt)}</td>
                      <td>
                        {k.status === 'active'
                          ? <Pill tone="ok">Active</Pill>
                          : <Pill>Retired {k.retiredAt ? dateTime(k.retiredAt) : ''}</Pill>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="row" style={{ marginTop: 12, alignItems: 'center' }}>
            <div className="small muted">Public keys: /.well-known/wekalp-ledger-keys.json?issuer={s.issuer.id}</div>
            {canRotate && (
              <div className="tight"><button onClick={() => setConfirming(true)}>Rotate keys</button></div>
            )}
          </div>
        </>
      )}
      {confirming && (
        <Modal
          title="Rotate signing keys"
          description="New ES256 and ML-DSA-65 keys sign every event from now on. The current keys are retired and stay published, so receipts already issued still check."
          onClose={() => setConfirming(false)}
          footer={(
            <>
              <button onClick={() => setConfirming(false)}>Cancel</button>
              <button className="primary" onClick={rotate} disabled={rotating}>{rotating ? 'Rotating…' : 'Rotate keys'}</button>
            </>
          )}
        >
          <p className="small">The change is recorded in the audit log.</p>
        </Modal>
      )}
    </Card>
  );
}
